import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { suppliersApi } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
    Truck,
    RefreshCw,
    Plus,
    Edit,
    Trash2,
    Phone,
    Mail,
    MapPin,
    ShieldCheck,
    Package
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, cn } from '../lib/utils';

const Suppliers = () => {
    const { isManagerOrAdmin: isManager } = useAuth();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    
    // Form state
    const [name, setName] = useState('');
    const [contactName, setContactName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [address, setAddress] = useState('');
    const [products, setProducts] = useState('');
    const [certified, setCertified] = useState(false);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        loadSuppliers();
    }, []);

    const loadSuppliers = async () => {
        try {
            const response = await suppliersApi.getAll();
            setSuppliers(response.data);
        } catch (error) {
            console.error('Failed to load suppliers:', error);
            toast.error('Failed to load suppliers');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setName('');
        setContactName('');
        setPhone('');
        setEmail('');
        setAddress('');
        setProducts('');
        setCertified(false);
        setNotes('');
        setEditingSupplier(null);
    };

    const openEditDialog = (supplier) => {
        setEditingSupplier(supplier);
        setName(supplier.name);
        setContactName(supplier.contact_name || '');
        setPhone(supplier.phone || '');
        setEmail(supplier.email || '');
        setAddress(supplier.address || '');
        setProducts(supplier.products?.join(', ') || '');
        setCertified(supplier.food_safety_certified);
        setNotes(supplier.notes || '');
        setDialogOpen(true);
    };

    const handleSubmit = async () => {
        if (!name) {
            toast.error('Please enter supplier name');
            return;
        }

        const supplierData = {
            name,
            contact_name: contactName || null,
            phone: phone || null,
            email: email || null,
            address: address || null,
            products: products ? products.split(',').map(p => p.trim()).filter(Boolean) : [],
            food_safety_certified: certified,
            notes: notes || null
        };

        try {
            if (editingSupplier) {
                const response = await suppliersApi.update(editingSupplier.id, supplierData);
                setSuppliers(suppliers.map(s => s.id === editingSupplier.id ? response.data : s));
                toast.success('Supplier updated successfully');
            } else {
                const response = await suppliersApi.create(supplierData);
                setSuppliers([response.data, ...suppliers]);
                toast.success('Supplier added successfully');
            }
            setDialogOpen(false);
            resetForm();
        } catch (error) {
            console.error('Failed to save supplier:', error);
            toast.error('Failed to save supplier');
        }
    };

    const handleDelete = async (supplier) => {
        if (!window.confirm(`Are you sure you want to delete ${supplier.name}?`)) return;

        try {
            await suppliersApi.delete(supplier.id);
            setSuppliers(suppliers.filter(s => s.id !== supplier.id));
            toast.success('Supplier deleted successfully');
        } catch (error) {
            console.error('Failed to delete supplier:', error);
            toast.error('Failed to delete supplier');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <RefreshCw className="h-8 w-8 brand-icon animate-spin" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="suppliers-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl md:text-3xl font-bold text-slate-100">Suppliers</h1>
                        <p className="text-slate-400 mt-1">
                            Manage your approved food suppliers
                        </p>
                    </div>
                    {isManager && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button className="brand-btn" data-testid="add-supplier-button">
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add Supplier
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="text-slate-100">
                                        {editingSupplier ? 'Edit Supplier' : 'Add Supplier'}
                                    </DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 pt-4 max-h-[70vh] overflow-y-auto">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Supplier Name *</Label>
                                        <Input
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                            placeholder="e.g., ABC Foods Ltd"
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="supplier-name-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Contact Name</Label>
                                        <Input
                                            value={contactName}
                                            onChange={(e) => setContactName(e.target.value)}
                                            placeholder="e.g., John Smith"
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="supplier-contact-input"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Phone</Label>
                                            <Input
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder="e.g., 01onal 234567"
                                                className="bg-slate-950 border-slate-800 text-slate-200"
                                                data-testid="supplier-phone-input"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300">Email</Label>
                                            <Input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="email@supplier.com"
                                                className="bg-slate-950 border-slate-800 text-slate-200"
                                                data-testid="supplier-email-input"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Address</Label>
                                        <Textarea
                                            value={address}
                                            onChange={(e) => setAddress(e.target.value)}
                                            placeholder="Full address"
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="supplier-address-input"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Products (comma separated)</Label>
                                        <Input
                                            value={products}
                                            onChange={(e) => setProducts(e.target.value)}
                                            placeholder="e.g., Milk, Bread, Eggs"
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="supplier-products-input"
                                        />
                                    </div>
                                    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
                                        <div>
                                            <Label className="text-slate-300">Food Safety Certified</Label>
                                            <p className="text-xs text-slate-500">Has valid food safety certification</p>
                                        </div>
                                        <Switch
                                            checked={certified}
                                            onCheckedChange={setCertified}
                                            data-testid="supplier-certified-switch"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300">Notes</Label>
                                        <Textarea
                                            value={notes}
                                            onChange={(e) => setNotes(e.target.value)}
                                            placeholder="Any additional notes..."
                                            className="bg-slate-950 border-slate-800 text-slate-200"
                                            data-testid="supplier-notes-input"
                                        />
                                    </div>
                                    <Button
                                        onClick={handleSubmit}
                                        className="w-full brand-btn"
                                        data-testid="supplier-submit-button"
                                    >
                                        {editingSupplier ? 'Update Supplier' : 'Add Supplier'}
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>

                {/* Suppliers Grid */}
                {suppliers.length > 0 ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {suppliers.map(supplier => (
                            <Card key={supplier.id} className="bg-slate-900 border-slate-800 card-hover" data-testid={`supplier-card-${supplier.id}`}>
                                <CardContent className="pt-6">
                                    <div className="flex items-start justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="h-12 w-12 rounded-xl brand-bg-subtle flex items-center justify-center">
                                                <Truck className="h-6 w-6 brand-icon" />
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-slate-100">{supplier.name}</h3>
                                                {supplier.contact_name && (
                                                    <p className="text-sm text-slate-400">{supplier.contact_name}</p>
                                                )}
                                            </div>
                                        </div>
                                        {supplier.food_safety_certified && (
                                            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">
                                                <ShieldCheck className="h-3 w-3 mr-1" />
                                                Certified
                                            </Badge>
                                        )}
                                    </div>

                                    <div className="space-y-2 text-sm">
                                        {supplier.phone && (
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <Phone className="h-4 w-4" />
                                                {supplier.phone}
                                            </div>
                                        )}
                                        {supplier.email && (
                                            <div className="flex items-center gap-2 text-slate-400">
                                                <Mail className="h-4 w-4" />
                                                {supplier.email}
                                            </div>
                                        )}
                                        {supplier.address && (
                                            <div className="flex items-start gap-2 text-slate-400">
                                                <MapPin className="h-4 w-4 mt-0.5" />
                                                <span className="line-clamp-2">{supplier.address}</span>
                                            </div>
                                        )}
                                    </div>

                                    {supplier.products?.length > 0 && (
                                        <div className="mt-4">
                                            <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                                                <Package className="h-3 w-3" />
                                                Products
                                            </div>
                                            <div className="flex flex-wrap gap-1">
                                                {supplier.products.slice(0, 5).map((product, idx) => (
                                                    <Badge key={idx} variant="secondary" className="bg-slate-800 text-slate-400 text-xs">
                                                        {product}
                                                    </Badge>
                                                ))}
                                                {supplier.products.length > 5 && (
                                                    <Badge variant="secondary" className="bg-slate-800 text-slate-500 text-xs">
                                                        +{supplier.products.length - 5} more
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {isManager && (
                                        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-800">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditDialog(supplier)}
                                                className="flex-1 text-slate-400 hover:text-slate-200"
                                                data-testid={`edit-supplier-${supplier.id}`}
                                            >
                                                <Edit className="h-4 w-4 mr-1" />
                                                Edit
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(supplier)}
                                                className="text-slate-400 hover:text-rose-400"
                                                data-testid={`delete-supplier-${supplier.id}`}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <Card className="bg-slate-900 border-slate-800">
                        <CardContent className="py-12">
                            <div className="empty-state">
                                <Truck className="empty-state-icon" />
                                <p className="empty-state-title">No suppliers</p>
                                <p className="empty-state-description">
                                    {isManager ? 'Add your first supplier to track approved food providers.' : 'No suppliers have been added yet.'}
                                </p>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
};

export default Suppliers;
